/**
 * Workflow SSN Verification — main component.
 *
 * Mirrors WorkflowPhoneVerification.tsx architecture.
 * User enters their 9-digit SSN, which is submitted to WE and polled to completion.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: SSN entry screen shown, waiting for user to submit
 * - submitting: Calling WE _input API + polling
 * - completed: WE returned COMPLETED, postMessage sent to parent
 * - error: Something failed
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { submitWorkflowInput, getWorkflowExecution } from "../../shared/api/workflowEngine";
import { WorkflowSsnScreen } from "./WorkflowSsnScreen";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { logger } from "../../shared/utils/logger";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

export type WorkflowSsnState = "initializing" | "ready" | "submitting" | "completed" | "error";

interface InitPayload {
  executionId: string;
  workflowId: number;
  inspectionName: string;
  authToken?: string;
  apiBaseUrl?: string;
}

const DEFAULT_API_BASE = "https://enroll.idme.test/api/workflow";

export function WorkflowSsnVerification() {
  const { t } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [state, setState] = useState<WorkflowSsnState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string>("*");

  // Derive and validate parent origin from document.referrer on mount
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[WorkflowSsn] Parent origin validated:", origin);
      } else {
        logger.warn("[WorkflowSsn] Parent origin not in allowlist:", origin);
      }
    } catch {
      logger.warn("[WorkflowSsn] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  // ── Error helper ──────────────────────────────────────────────────────
  const handleVerificationError = useCallback((errorCode: string, message: string) => {
    setError(message);
    setState("error");
    logger.error(`[WorkflowSsn] Error [${errorCode}]:`, message);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode, message, executionId: initDataRef.current?.executionId },
      },
      parentOriginRef.current
    );
  }, []);

  // ── SSN submit flow: _input → poll ───────────────────────────────────
  const handleSsnSubmit = useCallback(async (ssn: string) => {
    const data = initDataRef.current;
    if (!data) return;

    setState("submitting");

    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;

    try {
      logger.log("[WorkflowSsn] Submitting _input to WE");
      await submitWorkflowInput(
        apiBase,
        data.workflowId,
        data.executionId,
        {
          inspectionName: data.inspectionName,
          input: {
            ssn,
            inputType: "ssn",
            executionId: data.executionId,
          },
        },
        data.authToken
      );

      // Poll until SSN step completes (WE reaches COMPLETED, FAILED, CANCELLED,
      // or advances to a different inspection).
      logger.log("[WorkflowSsn] Polling until SSN step completes");
      const intervalMs = 2000;
      const timeoutMs = 120_000;
      const deadline = Date.now() + timeoutMs;

      const isSsnStepActive = (e: Awaited<ReturnType<typeof getWorkflowExecution>>) =>
        e.status === "PENDING_ASYNC" ||
        e.status === "RUNNING" ||
        e.status === "PENDING" ||
        (e.status === "WAITING_FOR_INPUT" && e.currentInspection === data.inspectionName);

      let exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);

      while (isSsnStepActive(exec) && Date.now() < deadline) {
        logger.log("[WorkflowSsn] Still", exec.status, `(${exec.currentInspection ?? "?"}) — polling`);
        await new Promise((r) => setTimeout(r, intervalMs));
        exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);
      }

      if (Date.now() >= deadline) {
        handleVerificationError("poll_timeout", "Verification timed out waiting for SSN step");
        return;
      }

      logger.log("[WorkflowSsn] SSN step done, status:", exec.status, `(${exec.currentInspection ?? "?"})`);

      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        const msg =
          (exec.result?.data as Record<string, unknown>)?.message as string ??
          "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }

      setState("completed");
    } catch (err) {
      logger.error("[WorkflowSsn] Submit flow failed:", err);
      handleVerificationError(
        "submit_failed",
        err instanceof Error ? err.message : "Verification processing failed"
      );
    }
  }, [handleVerificationError]);

  // ── No SSN: signal parent so it can dispatch 'no_ssn' to WE ─────────
  const handleNoSsn = useCallback(() => {
    const data = initDataRef.current;
    logger.log("[WorkflowSsn] User clicked no SSN");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_NO_SSN",
        payload: { executionId: data?.executionId, workflowId: data?.workflowId },
      },
      parentOriginRef.current
    );
  }, []);

  // ── Step 1: Send IDME_READY, listen for IDME_INIT ────────────────────
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      parentOriginRef.current
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[WorkflowSsn] IDME_INIT rejected from origin:", event.origin);
          return;
        }
      } catch {
        logger.warn("[WorkflowSsn] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[WorkflowSsn] Received init:", payload.executionId);
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Step 2: Send VERIFICATION_COMPLETE when WE completes ─────────────
  useEffect(() => {
    if (state !== "completed" || !initData) return;
    logger.log("[WorkflowSsn] Notifying parent of completion");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: {
          executionId: initData.executionId,
          workflowId: initData.workflowId,
          status: "completed",
        },
      },
      parentOriginRef.current
    );
  }, [state, initData]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(' ')}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
            </div>
            {consumerLogo && (
              <>
                <span className={styles.logoSeparator} aria-label="and">+</span>
                <div className={styles.logo}>
                  <img src={consumerLogo.src} alt={consumerLogo.alt} />
                </div>
              </>
            )}
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing */}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — SSN entry form */}
          {state === "ready" && (
            <WorkflowSsnScreen
              onSubmit={handleSsnSubmit}
              onNoSsn={handleNoSsn}
              isSubmitting={false}
            />
          )}

          {/* Processing — spinner */}
          {state === "submitting" && (
            <div className={styles.card}>
              <h2 className={styles.title}>{t("ssn.processing.title")}</h2>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Completed */}
          {state === "completed" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t("ssn.completed.title")}</h2>
              <p className={styles.subtitle}>{t("ssn.completed.subtitle")}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t("ssn.error.title")}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        <footer className={styles.footer}>
          <LanguageSelector />
          <div className={styles.footerLinks}>
            <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('ssn.footer.whatIsIdmeAriaLabel')}>{t('ssn.footer.whatIsIdme')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('ssn.footer.termsOfServiceAriaLabel')}>{t('ssn.footer.termsOfService')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('ssn.footer.privacyPolicyAriaLabel')}>{t('ssn.footer.privacyPolicy')}</a>
          </div>
        </footer>

      </div>
    </div>
  );
}
